import type { AxiosError } from "axios";

type StatusCode = 400 | 401 | 403 | 404 | 500 | 502 | 503 | 504;

function getHttpMessage(status?: StatusCode) {
  switch (status) {
    case 400:
      return "The request was invalid. Please check your input and try again.";
    case 401:
      return "Your session has expired. Please log in again.";
    case 403:
      return "You do not have permission to perform this action.";
    case 404:
      return "The requested resource could not be found.";
    case 500:
      return "Something went wrong on our server. Please try again later.";
    case 502:
      return "The server is temporarily unavailable. Please try again shortly.";
    case 503:
      return "The service is temporarily unavailable. Please try again shortly.";
    case 504:
      return "The server took too long to respond. Please try again.";
    default:
      return "Unexpected error occurred. Please try again.";
  }
}

export class AppError extends Error {
  constructor(
    public override message: string,
    public override name: string = "Application Error",
    public code?: string
  ) {
    super(message);
    this.name = name;
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
}

export class NetworkError extends AppError {
  private readonly statusCode: number;

  constructor(status: number, message?: string) {
    super(message ?? getHttpMessage(status as StatusCode), "Network Error");
    this.name = "Network Error";
    this.statusCode = status;
  }

  get status() {
    return this.statusCode;
  }
}

export class AuthError extends AppError {
  constructor(message: string = "Authentication failed") {
    super(message, "Authentication Error");
  }
}

/**
 * Utility to convert unidentified errors into structured AppError/NetworkError instances.
 */
export function handleError(error: unknown): AppError {
  if (error instanceof AppError) return error;

  if (error && typeof error === "object" && (error as any).isAxiosError) {
    const axiosError = error as any;
    if (!axiosError.response) {
      return new NetworkError(503, "The server is temporarily unavailable. Please check your connection or try again shortly.");
    }
    return new NetworkError(axiosError.response?.status || 500, axiosError.response?.data?.message);
  }

  // Plain Error carrying an HTTP status code (e.g. errors transformed by the
  // legacy `apiFetch` wrapper). Normalise these into a NetworkError so the rest
  // of the app can treat all backend failures consistently.
  if (error && typeof error === "object" && typeof (error as any).status === "number") {
    return new NetworkError((error as any).status, (error as any).response?.data?.message);
  }

  if (error instanceof Error) {
    return new AppError(error.message, error.name);
  }

  return new AppError("An unexpected error occurred");
}

/**
 * Predicate for errors that originate from a backend/network request and have
 * already been (or can be) normalised into a structured error. These are
 * "expected" failures (503, timeouts, offline, 5xx, etc.) that should be shown
 * to the user as a friendly notification rather than surfaced as an uncaught
 * runtime error overlay.
 */
export function isApiError(error: unknown): boolean {
  if (error instanceof AppError) return true;
  if (error && typeof error === "object") {
    const e = error as any;
    if (e.isAxiosError) return true;
    if (typeof e.status === "number") return true;
    if (typeof e.response?.status === "number") return true;
  }
  return false;
}

/**
 * Produce a user-facing, human-friendly message for any error coming from the
 * API layer. Falls back to a generic message for anything unrecognised.
 */
export function getUserFacingErrorMessage(error: unknown): string {
  if (error instanceof NetworkError) return error.message;

  const status = (error as any)?.status ?? (error as any)?.response?.status;
  if (typeof status === "number") return getHttpMessage(status as StatusCode);

  const appError = handleError(error);
  return appError.message || "Something went wrong. Please try again.";
}
